import siteConfig from "site-config";
import { formatMessage } from "lib/globalize-util";
import { COLLECTIONS } from "constants/action-types";
import {
    fetchBrandSuccess,
    fetchBrandError,
    fetchBrandsSuccess,
    fetchBrandsError,
    fetchSingleBrandError,
} from "actions/collections";
import { add as snackbarAdd } from "actions/snackbars";
import { ajax } from "lib/observable";
import "rxjs/add/operator/map";
import { Observable } from "rxjs";
import { updateEntities } from "redux-query";
import { Brands as BrandsSchema } from "schemas/dashboard";
import { ChatMessage as ChatMessagesSchema } from "schemas/collections";
import { getRecordsFromSchema, normalizeData } from "lib/api";
import me from "models/me";
import { activeBrandSet } from "actions/scope";
import { stringify } from "query-string";
import { ROOT_NAMESPACE } from "constants/product-collection";
import { BrandRecord } from "records/brand";

export const fetchBrandsForCollectionSidePanelEpic = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION_SIDE_PANEL.BRANDS.FETCH.START)
        .map((payload) => payload)
        .switchMap(({ payload }) => {
            if (!me.isSignedIn()) {
                return [];
            }
            return ajax({
                method: "GET",
                url: `${siteConfig.api}/api/v3.0/brands/list?${stringify(payload)}`,
            })
                .map(({ response, status }) => {
                    const brandRecords = getRecordsFromSchema(BrandsSchema);
                    const { entities, result } = normalizeData(response, BrandsSchema, brandRecords);
                    return {
                        entities,
                        brands: result,
                    };
                })
                .takeUntil(action$.ofType(COLLECTIONS.COLLECTION_SIDE_PANEL.BRANDS.FETCH.CANCEL))
                .mergeMap(({ brands, entities }) => [
                    updateEntities({
                        brands: (currentBrands) => {
                            return {
                                ...(currentBrands?.loading ? {} : currentBrands),
                                ...entities.brand,
                            };
                        },
                    }),
                    fetchBrandsSuccess(brands),
                ])
                .catch((error) => {
                    if (!me.isSignedIn()) {
                        return [];
                    }
                    return [
                        fetchBrandsError(error),
                        snackbarAdd({ message: formatMessage("There was an error fetching your brands") }),
                    ];
                });
        });

export const fetchSingleBrandForCollectionSidePanelEpic = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION_SIDE_PANEL.SINGLE_BRAND.FETCH.START)
        .pluck("payload")
        .switchMap(({ brandId, forceNotFound }) => {
            if (!brandId && !me.last_brand) {
                return [fetchSingleBrandError()];
            }

            if (!brandId) {
                const brandRecord = new BrandRecord({ name: formatMessage("Loading") });
                return [
                    updateEntities({
                        brands: (currentBrands) => ({
                            ...currentBrands,
                            loading: brandRecord,
                        }),
                    }),
                    fetchBrandsSuccess([""]),
                ];
            }
            if (!me.isSignedIn() || !brandId || !brandId.length) {
                return [];
            }

            const params =
                forceNotFound &&
                stringify({
                    __force_not_found: true,
                });
            const queryParams = params ? `?${params}` : "";

            return ajax({
                method: "GET",
                url: `${siteConfig.api}/brand/${brandId}${queryParams}`,
                handle401: true,
            })
                .map(({ response }) => {
                    const brandRecords = getRecordsFromSchema(BrandsSchema);
                    const { entities, result } = normalizeData([response], BrandsSchema, brandRecords);
                    return {
                        entities,
                        brands: result,
                    };
                })
                .takeUntil(action$.ofType(COLLECTIONS.COLLECTION_SIDE_PANEL.BRANDS.FETCH.CANCEL))
                .mergeMap(({ brands, entities }) => {
                    return [
                        updateEntities({
                            brands: (currentBrands) => ({
                                ...currentBrands,
                                ...entities.brand,
                            }),
                        }),
                        fetchBrandsSuccess(brands),
                    ];
                })
                .catch(() => {
                    if (!me.isSignedIn()) {
                        return [];
                    }
                    return [
                        ...(!forceNotFound
                            ? [snackbarAdd({ message: formatMessage("There was an error fetching your brand") })]
                            : []),
                        fetchSingleBrandError(),
                    ];
                });
        });
export const fetchBrandForCollectionSidePanelEpic = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION_SIDE_PANEL.BRAND.FETCH.START)
        .map(({ payload }) => payload)
        .mergeMap((payload) => {
            return ajax({
                method: "GET",
                url: `${siteConfig.api}/brand/${payload}`,
            })
                .map(({ response, status }) => ({
                    filters: [{ field: "BRAND_ID", value: response._id }],
                    firstResponse: response,
                    status,
                }))
                .mergeMap(({ firstResponse, status, filters }) => {
                    return ajax({
                        body: { filters },
                        hideBeforeUnloadWarning: true,
                        method: "POST",
                        url: `${siteConfig.goAPI}/v1/pages_search`,
                    })
                        .map(({ response }) => {
                            const transformedResponse = [firstResponse].map((brand) => {
                                const brandPage = response.data.find((page) => page.brand_id === brand._id) || {};

                                return {
                                    ...brand,
                                    picture: brandPage.picture,
                                };
                            });
                            const brandRecords = getRecordsFromSchema(BrandsSchema);
                            const { entities, result } = normalizeData(transformedResponse, BrandsSchema, brandRecords);
                            return {
                                entities,
                                brands: result,
                            };
                        })
                        .mergeMap(({ brands, entities }) => {
                            let actions = [
                                updateEntities({
                                    brands: (currentBrands) => ({
                                        ...currentBrands,
                                        ...entities.brand,
                                    }),
                                }),
                                fetchBrandSuccess(),
                            ];

                            if (status === 200) {
                                actions = actions.concat(activeBrandSet(firstResponse));
                            } else {
                                const { scope } = store.getState();
                                actions = actions.concat(activeBrandSet(scope.brand));
                            }

                            return actions;
                        })
                        .catch(() => [
                            snackbarAdd({ message: formatMessage("Error fetching brand picture") }),
                            fetchBrandError(),
                        ]);
                })
                .catch(() => [snackbarAdd({ message: formatMessage("Error fetching brand") }), fetchBrandError()]);
        });

export const fetchCollectionChatMessages = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION_CHAT.FETCH_MESSAGES.START)
        .map(({ payload }) => payload)
        .mergeMap((payload) => {
            // eslint-disable-next-line prefer-const
            let { collectionId, options = {} } = payload;
            const { merchantId } = store.getState().collection;
            let queryOptions = "";
            const limit = options.limit;
            if (options) {
                options = {
                    ...options,
                    merchant_id: merchantId || me.getLastMerchantId(),
                };
                queryOptions = stringify(options);
            }

            return ajax({
                method: "GET",
                url: `${siteConfig.goAPI}/v1/collection/${collectionId}/chat_messages?${queryOptions}`,
            })
                .map(({ response }) => {
                    const formattedResponse = {
                        messages: response.data,
                        key: `${ROOT_NAMESPACE}-${collectionId}`,
                    };
                    const existingEntities = store.getState();
                    const { chatMessages } = existingEntities.entities;
                    if (chatMessages && chatMessages[formattedResponse.key]) {
                        const existingThread = chatMessages[formattedResponse.key];
                        const existingMessages = existingThread.messages.toJS();
                        formattedResponse.messages = existingMessages.concat(formattedResponse.messages);
                    }

                    formattedResponse.messages = formattedResponse.messages.filter((message) => {
                        return message.id;
                    });

                    const meta = {
                        hasMore: response.data.length === limit,
                    };

                    const records = getRecordsFromSchema(ChatMessagesSchema);
                    const { entities, result } = normalizeData(formattedResponse, ChatMessagesSchema, records);

                    return {
                        entities,
                        result,
                        meta,
                    };
                })
                .mergeMap(({ entities, result, meta }: any) => {
                    return [
                        updateEntities({
                            chatMessages: (messages = {}) => {
                                return {
                                    ...messages,
                                    ...entities.chatMessages,
                                };
                            },
                        }),
                        {
                            payload: [result],
                            type: COLLECTIONS.COLLECTION_CHAT.FETCH_MESSAGES.COMPLETED,
                            meta,
                        },
                    ];
                })
                .catch((error) => {
                    return Observable.of({
                        error: true,
                        payload: error,
                        type: COLLECTIONS.COLLECTION_CHAT.FETCH_MESSAGES.COMPLETED,
                    });
                });
        });

export const createCollectionChatMessages = (action$, store) =>
    action$.ofType(COLLECTIONS.COLLECTION_CHAT.CREATE_MESSAGE.START).mergeMap(({ payload, meta }) => {
        const { collectionId, data } = payload;
        const { merchantId } = store.getState().collection;
        return ajax({
            method: "POST",
            url: `${siteConfig.goAPI}/v1/collections/${collectionId}/chat_messages`,
            body: { ...data, merchant_id: merchantId },
        })
            .map(({ response }) => {
                const { entities } = store.getState();
                const chatMessages = entities.chatMessages;
                const chatThreadEntity = chatMessages[`${meta.namespace}-${collectionId}`];
                const updatedMessages = chatThreadEntity.messages.toJS();
                // Server does not respond with a full 'message' object, so we have to rebuild
                const message = {
                    timestamp_ms: response.timestamp_ms,
                    id: response.id,
                    thread_id: collectionId,
                    namespace: meta.namespace,
                    identity: meta.identity,
                    identity_type: "USER",
                    kind: data.kind,
                    content: data.content,
                };
                updatedMessages.unshift(message);

                const formattedResponse = {
                    messages: updatedMessages,
                    key: `${meta.namespace}-${collectionId}`,
                };
                const records = getRecordsFromSchema(ChatMessagesSchema);
                const processedData = normalizeData(formattedResponse, ChatMessagesSchema, records);

                return {
                    original_messages: chatThreadEntity.messages.toJS(),
                    result: processedData.result,
                    entities: processedData.entities,
                };
            })
            .mergeMap(({ result, entities, original_messages }: any) => {
                const actions = [
                    {
                        payload: [result],
                        type: COLLECTIONS.COLLECTION_CHAT.CREATE_MESSAGE.COMPLETED,
                    },
                    {
                        meta: {
                            reset: true,
                        },
                        type: COLLECTIONS.COLLECTION_CHAT.UPDATES.RECEIVED,
                    },
                ];

                // if (!original_messages.length) {
                //     actions.push(
                //         updateEntities({
                //             chatMessages: (messages = {}) => {
                //                return {
                //                     ...messages,
                //                     ...entities.chatMessages,
                //                 };
                //             },
                //         }),
                //     );
                // }

                return actions;
            })
            .catch((error) => {
                return Observable.of(
                    {
                        error: true,
                        payload: error,
                        type: COLLECTIONS.COLLECTION_CHAT.CREATE_MESSAGE.COMPLETED,
                    },
                    {
                        meta: {
                            reset: true,
                        },
                        type: COLLECTIONS.COLLECTION_CHAT.UPDATES.RECEIVED,
                    }
                );
            });
    });

export const deleteCollectionChatMessages = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION_CHAT.DELETE_MESSAGE.START)
        .map(({ payload }) => payload)
        .mergeMap((payload) => {
            const { chatThreadId, messageId, collectionId } = payload;
            const { merchantId } = store.getState().collection;
            return ajax({
                method: "DELETE",
                url: `${siteConfig.goAPI}/v1/collections/${collectionId}/chat_messages/${messageId}?merchant_id=${merchantId}`,
            })
                .map(({ response }) => {
                    const { entities } = store.getState();
                    const chatMessages = entities.chatMessages;
                    let chatThreadEntity = chatMessages[chatThreadId];
                    chatThreadEntity = chatThreadEntity.toJS();
                    chatThreadEntity.messages = chatThreadEntity.messages.filter((message) => {
                        message.timestamp_ms = message.timestamp_ms.valueOf().toString();
                        return message.id !== messageId;
                    });

                    const records = getRecordsFromSchema(ChatMessagesSchema);
                    const processedData = normalizeData(chatThreadEntity, ChatMessagesSchema, records);
                    return {
                        entities: processedData.entities,
                        result: processedData.result,
                    };
                })
                .mergeMap(({ entities, result }: any) => {
                    return [
                        updateEntities({
                            chatMessages: (messages = {}) => {
                                return {
                                    ...messages,
                                    ...entities.chatMessages,
                                };
                            },
                        }),
                        {
                            payload: [result],
                            type: COLLECTIONS.COLLECTION_CHAT.DELETE_MESSAGE.COMPLETED,
                        },
                    ];
                })
                .catch((error) => {
                    if (error.status === 404) {
                        // already deleted, just updated entities
                        const { entities } = store.getState();
                        const chatMessages = entities.chatMessages;
                        let chatThreadEntity = chatMessages[chatThreadId];
                        chatThreadEntity = chatThreadEntity.toJS();
                        chatThreadEntity.messages = chatThreadEntity.messages.filter((message) => {
                            message.timestamp_ms = message.timestamp_ms.valueOf().toString();
                            return message.id !== messageId;
                        });
                        const records = getRecordsFromSchema(ChatMessagesSchema);
                        const processedData = normalizeData(chatThreadEntity, ChatMessagesSchema, records);

                        return Observable.merge(
                            Observable.of(
                                updateEntities({
                                    chatMessages: (messages = {}) => {
                                        return {
                                            ...messages,
                                            ...processedData.entities.chatMessages,
                                        };
                                    },
                                })
                            ),
                            Observable.of({
                                payload: [processedData.result],
                                type: COLLECTIONS.COLLECTION_CHAT.DELETE_MESSAGE.COMPLETED,
                            })
                        );
                    } else {
                        return Observable.merge(
                            Observable.of(
                                snackbarAdd({ message: formatMessage("There was an error deleting your message") })
                            )
                        );
                    }
                });
        });

export const unsubscribeCollectionChatEpic = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION_CHAT.UPDATES.UNSUBSCRIBE)
        .map(({ payload }) => payload)
        .mergeMap((payload) => {
            const actions = [];
            // when user has nav'd away from a collection, for simplicity, just remove
            // that assortment's chat thread from entities
            if (payload) {
                const { namespace, id } = payload;
                if (namespace && id) {
                    actions.push(
                        updateEntities({
                            chatMessages: (chatThreads = {}) => {
                                const key = `${namespace}-${id}`;
                                delete chatThreads[key];
                                return {
                                    ...chatThreads,
                                };
                            },
                        })
                    );
                }
            }
            return actions;
        });
